import { useContext } from 'react';
import { RappidContext } from '../../Diagram';
import { update_SpecReactions } from '../../../Simulator/services/specReactionService';

const LOCATION_TYPES = {
    NODE: 'NODE',
    EDGE: 'EDGE'
};

const createEmptyChanges = () => ({
    nodes: new Set(),
    edges: new Set(),
    components: new Set()
});

export const createAgMatrix = (initialData = null) => {
    return {
        nodes: new Map(),
        edges: new Map(),
        adjacencyMatrix: new Map(),
        unitArray: [],
        componentMatrix: [],
        componentList: [],
        molecularWeights: {},
        changes: createEmptyChanges()
    };
};

const componentMatrixToArray = (componentMatrix, unitArray, componentList, molecularWeights) => 
    unitArray.map((unitId, unitIndex) => {
        const row = { 'Unit ID': unitId };
        componentList.forEach((component, componentIndex) => {
            if (component !== undefined) {
                row[component] = componentMatrix[unitIndex][componentIndex] 
                    ? `Present (MW: ${molecularWeights[component] || 'N/A'})` 
                    : 'Absent';
            }
        });
        return row;
    });

const adjacencyMatrixToArray = (adjacencyMatrix, unitArray) => {
    return unitArray.map(sourceId => {
        const row = { 'Unit ID': sourceId };
        unitArray.forEach(targetId => {
            row[targetId] = adjacencyMatrix.get(sourceId)?.get(targetId) || 0;
        });
        return row;
    });
};

const matrixToArray = (matrix, unitArray) => {
    return unitArray.map(sourceId => {
        const row = { 'Unit ID': sourceId };
        unitArray.forEach(targetId => {
            row[targetId] = matrix.get(sourceId)?.get(targetId) || 0;
        });
        return row;
    });
};

const intaialiseAdjacencyMatrix = (initialData = null, rappid) => {
    if (!rappid) throw new Error("Rappid context is not available.");

    const matrix = createAgMatrix(initialData);
    const graph = rappid.getGraph();
    const links = graph.getLinks();
    const units = graph.getElements();

    matrix.unitArray = units
        .filter(unit => unit.attributes['unitType'] != null)
        .map(unit => {
            const id = unit.attributes.id;
            matrix.nodes.set(id, {
                id,
                type: unit.attributes['unitType'],
                attributes: { ...unit.attributes }
            });
            return id;
        });

    matrix.unitArray.forEach(unitId => matrix.adjacencyMatrix.set(unitId, new Map()));
    links.forEach(link => {
        const sourceId = link.attributes?.source?.id;
        const targetId = link.attributes?.target?.id;

        if (matrix.adjacencyMatrix.has(sourceId) && matrix.adjacencyMatrix.has(targetId)) {
            const currentValue = matrix.adjacencyMatrix.get(sourceId).get(targetId) || 0;
            matrix.adjacencyMatrix.get(sourceId).set(targetId, currentValue + 1);
            const linkId = `${sourceId}-${targetId}`;
            matrix.edges.set(linkId, { id: linkId, source: sourceId, target: targetId, attributes: { ...link.attributes } });
        } else {
            console.warn("Source or target ID not found in unit array:", { sourceId, targetId });
        }
    });

    intialiseAndCollectComponents(units, matrix);

    return matrix;
};

export const intialiseAndCollectComponents = (units, matrix) => {
    const componentSet = new Set();
    matrix.componentMatrix = matrix.unitArray.map(() => new Array(0).fill(false));

    matrix.unitArray.forEach(unitId => {
        const unit = units.find(u => u.attributes.id === unitId);

        //collect components from feed units
        if (unit && unit.attributes['unitType'] === 'Feed' && unit.attributes['Fd_components']) {
            const components = unit.attributes['Fd_components'];
            Object.keys(components).forEach(component => {
                if (components[component] !== undefined) {
                    componentSet.add(component);
                }
            });
        }

        //if unit is not a feed but has a reaction, collect components
        //basically collect the RX_components as components, so that the vessel can be established as the
        //source of the component
        //might not have to copy the normal compotents?
        if(unit && unit.attributes['reaction'] === 'Yes' && unit.attributes['RX_components']){
            const components = unit.attributes['RX_components'];
            Object.keys(components).forEach(component => {
                if (components[component] !== undefined) {
                    componentSet.add(component);
                }
            });
        }
    });

    matrix.componentList = Array.from(componentSet);

    matrix.componentMatrix = matrix.unitArray.map(() =>
        new Array(matrix.componentList.length).fill(false)
    );

    matrix.unitArray.forEach((unitId, unitIndex) => {
        const unit = units.find(u => u.attributes.id === unitId);
        // Handle Feed components
        if (unit && unit.attributes['unitType'] === 'Feed' && unit.attributes['Fd_components']) {
            const components = unit.attributes['Fd_components'];
            Object.entries(components).forEach(([component, componentData]) => {
                if (componentData !== undefined) {
                    matrix.molecularWeights[component] = matrix.molecularWeights[component] ||
                        parseFloat(componentData.M) || 0;
                    
                    const componentIndex = matrix.componentList.indexOf(component);
                    if (componentIndex !== -1) {
                        matrix.componentMatrix[unitIndex][componentIndex] = true;
                    }
                }
            });
        }
        // Handle Reaction components
        if (unit && unit.attributes['reaction'] === 'Yes' && unit.attributes['RX_components']) {
            const components = unit.attributes['RX_components'];
            Object.entries(components).forEach(([component, componentData]) => {
                if (componentData !== undefined) {
                    matrix.molecularWeights[component] = matrix.molecularWeights[component] ||
                        parseFloat(componentData.M) || 0;
                    
                    const componentIndex = matrix.componentList.indexOf(component);
                    if (componentIndex !== -1) {
                        matrix.componentMatrix[unitIndex][componentIndex] = true;
                    }
                }
            });
        }
    });

    return matrix;
};

export const convertToReachabilityMatrix = (adjacencyMatrix) => {
    let reachabilityMatrix = new Map();
    
    adjacencyMatrix.forEach((_, unitId) => {
        reachabilityMatrix.set(unitId, new Map());
    });

    adjacencyMatrix.forEach((connections, sourceId) => {
        connections.forEach((value, targetId) => {
            reachabilityMatrix.get(sourceId).set(targetId, value > 0 ? 1 : 0);
        });
    });

    adjacencyMatrix.forEach((_, k) => {
        adjacencyMatrix.forEach((_, i) => {
            adjacencyMatrix.forEach((_, j) => {
                const throughK = (reachabilityMatrix.get(i).get(k) && reachabilityMatrix.get(k).get(j)) ? 1 : 0;
                if (throughK) {
                    reachabilityMatrix.get(i).set(j, 1);
                }
            });
        });
    });

    return reachabilityMatrix;
};

export const propagateComponents = (matrix, reachabilityMatrix) => {
    console.log("Adjacency Matrix:");
    console.table(matrixToArray(matrix.adjacencyMatrix, matrix.unitArray));

    console.log("Reachability Matrix:");
    console.table(matrixToArray(reachabilityMatrix, matrix.unitArray));

    const propagatedComponentMatrix = matrix.componentMatrix.map(row => [...row]);

    matrix.unitArray.forEach((sourceId, sourceIndex) => {
        matrix.unitArray.forEach((targetId, targetIndex) => {
            if (reachabilityMatrix.get(sourceId).get(targetId)) {
                for (let i = 0; i < matrix.componentList.length; i++) {
                    if (matrix.componentMatrix[sourceIndex][i] && matrix.componentList[i] !== undefined) {
                        if (!propagatedComponentMatrix[targetIndex][i]) {
                            propagatedComponentMatrix[targetIndex][i] = true;
                            matrix.changes.components.add(targetId);
                        }
                    }
                }
            }
        });
    });

    console.log("Propagated Component Matrix (with Molecular Weights):");
    console.table(componentMatrixToArray(propagatedComponentMatrix, matrix.unitArray, matrix.componentList, matrix.molecularWeights));

    return { ...matrix, componentMatrix: propagatedComponentMatrix };
};

export const updateAgMatrixFromGraph = (matrix) => {
    const rappid = useContext(RappidContext);
    if (!rappid) {
        throw new Error("Rappid context is not available.");
    }
    const graph = rappid.getGraph();

    graph.getElements().forEach(element => {
        const unitId = element.attributes.id;
        if (matrix.nodes.has(unitId)) {
            matrix.nodes.set(unitId, {
                ...matrix.nodes.get(unitId),
                attributes: { ...element.attributes }
            });
        }
    });

    graph.getLinks().forEach(link => {
        const sourceId = link.attributes?.source?.id;
        const targetId = link.attributes?.target?.id;
        const linkId = `${sourceId}-${targetId}`;

        if (matrix.edges.has(linkId)) {
            matrix.edges.set(linkId, {
                ...matrix.edges.get(linkId),
                attributes: { ...link.attributes }
            });
        }
    });

    graph.getElements().forEach(element => {
        const unitId = element.attributes.id;
        if (element.attributes['unitType'] === 'Feed' && matrix.componentMatrix.has(unitId)) {
            const components = element.attributes['equipment_Properties']['Fd_components'] || [];
            matrix.componentMatrix.set(unitId, new Set(components.filter(comp => comp !== undefined)));
        }
    });

    const updatedMatrix = propagateComponents(matrix, convertToReachabilityMatrix(matrix.adjacencyMatrix));

    return updatedMatrix;
};

export const saveComponentsToGraph = (graph, matrix) => {
    matrix.unitArray.forEach((unitId, unitIndex) => {
        const unit = graph.getCell(unitId);
        if (unit) {
            const newComponents = {};

            matrix.componentList.forEach((component, componentIndex) => {
                if (matrix.componentMatrix[unitIndex][componentIndex] && component !== undefined) {
                    const componentKey = component.replace(/\s/g, '_');
                    
                    if (unit.attributes.components?.[componentKey] !== undefined) {
                        newComponents[componentKey] = {
                            ...unit.attributes.components[componentKey],
                            M: matrix.molecularWeights[component] || unit.attributes.components[componentKey].M || ''
                        };
                    } else {
                        newComponents[componentKey] = {
                            M: matrix.molecularWeights[component] || ''
                        };
                    }
                }
            });
            
            if (Object.keys(newComponents).length === 0) {
                unit.unset('components');
                console.log(`All components removed from unit ${unitId}`);
            } else {
                unit.set('components', newComponents);
            }

            //clean up rx_data
            if (unit.attributes['equipment_Properties'] && 
                unit.attributes['equipment_Properties']['rx_Data']) {
                
                const rx_Data = unit.attributes['equipment_Properties']['rx_Data'];
                const updatedRxData = {};
                
                // Only keep rx_Data entries for components that still exist
                Object.entries(rx_Data).forEach(([componentKey, data]) => {
                    if (newComponents[componentKey] !== undefined) {
                        updatedRxData[componentKey] = data;
                    }
                });
                
                unit.attributes['equipment_Properties']['rx_Data'] = updatedRxData;

                // Add new components to rx_Data
                update_SpecReactions(Object.keys(newComponents), updatedRxData, matrix.molecularWeights);
            }
            
            //remove from basis if it's selected
            if (unit.attributes['equipment_Properties'] && 
                unit.attributes['equipment_Properties']['basis']) {
                
                const basis = unit.attributes['equipment_Properties']['basis'];
                
                // If the basis component no longer exists in newComponents, reset it
                if (basis && newComponents[basis] === undefined) {
                    // Either set to null/empty
                    unit.attributes['equipment_Properties']['basis'] = '';
                    
                    // Or set to the first available component (if any exist)
                    if (Object.keys(newComponents).length > 0) {
                        unit.attributes['equipment_Properties']['basis'] = Object.keys(newComponents)[0];
                    }
                }
            }


            if (matrix.changes.components.has(unitId)) {
                matrix.changes.nodes.add(unitId);
            }
        }
    });
};

export const updateLinkAttributes = (graph, matrix) => {
    graph.getLinks().forEach(link => {
        const sourceUnit = graph.getCell(link.attributes.source.id);
        const targetUnit = graph.getCell(link.attributes.target.id);

        if (sourceUnit && targetUnit) {
            const linkComponents = {};
            Object.keys(sourceUnit.attributes.components || {}).forEach(component => {
                if (targetUnit.attributes.components?.[component] !== undefined &&
                    sourceUnit.attributes.components[component] !== undefined) {
                    linkComponents[component] = sourceUnit.attributes.components[component];
                }
            });

            if (JSON.stringify(link.attributes.components) !== JSON.stringify(linkComponents)) {
                link.set('components', linkComponents);
                matrix.changes.edges.add(link.id);

                if (link.attributes.specified_Stream_Properties) {
                    link.unset('specified_Stream_Properties');
                }
            }
        }
    });
};

export const updateLinksWithAgMatrix = (graph, rappid) => {
    let matrix = createAgMatrix();
    matrix = intaialiseAdjacencyMatrix(matrix, rappid);
    const reachabilityMatrix = convertToReachabilityMatrix(matrix.adjacencyMatrix);
    const propagatedMatrix = propagateComponents(matrix, reachabilityMatrix);
    saveComponentsToGraph(graph, propagatedMatrix);
    updateLinkAttributes(graph, propagatedMatrix);
    return propagatedMatrix;
};